<template>
    <DCardBody>
        <DForm @submit.prevent="submitForm">
            <input
                type="hidden"
                name="id"
                :value="form.id"
            >
            <DFormRow
                :label="__('settings.tax_exemptions.form.name')"
                :is-required="true"
                :hint="__('settings.tax_exemptions.form.name_hint')"
            >
                <DInput
                    id="name"
                    v-model="form.name"
                    add-label-classes="required"
                    maxlength="45"
                />
                <DAlert
                    v-if="errors && errors.name"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.name }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.tax_exemptions.form.invoice_text')"
                :is-required="true"
                :hint="__('settings.tax_exemptions.form.invoice_text_hint')"
            >
                <DInput
                    id="invoice_text"
                    v-model="form.invoice_text"
                    add-label-classes="required"
                    maxlength="255"
                />
                <DAlert
                    v-if="errors && errors.invoice_text"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.invoice_text }}
                </DAlert>
                <TranslationLink
                    :tenant-languages="tenantLanguages"
                    :route="$route('settings.tax-exemption.translations', {taxExemption: toEdit?.id ?? 0})"
                    :default-tenant-language="defaultTenantLanguage"
                />
            </DFormRow>

            <DynamicTable
                v-model="form.tax_exemption_rates"
                :fields="getFields"
                :transformers="transformers"
                :new-row-data="getNewRowData()"
                :modal-title-add="__('settings.tax_exemptions.modal.title_add')"
                :modal-title-edit="__('settings.tax_exemptions.modal.title_edit')"
                :errors="errorsData"
                error-prefix="tax_exemption_rates"
                @update:modelValue="setExemptionRates"
            >
                <template #modalForm="{ item }">
                    <DFormRow
                        :label="__('settings.tax_exemptions.form.valid_from')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.valid_from`]"
                        :hint="__('settings.tax_exemptions.form.valid_from_hint')"
                    >
                        <DDateFormatInput
                            id="valid_from"
                            v-model="item.valid_from"
                            format="YYYY-MM-DD"
                            type="date"
                        />
                    </DFormRow>
                    <DFormRow
                        class="mb-4"
                        :label="__('settings.tax_exemptions.form.no_tax_on_invoice')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.no_tax_on_invoice`]"
                        :hint="__('settings.tax_exemptions.form.no_tax_on_invoice_hint')"
                    >
                        <DFormCheck
                            id="no_tax_on_invoice"
                            v-model="item.no_tax_on_invoice"
                            inline
                        />
                    </DFormRow>
                    <DFormRow
                        v-if="item.no_tax_on_invoice === false"
                        :label="__('settings.tax_exemptions.form.tax_rate')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.tax_rate`]"
                        :hint="__('settings.tax_exemptions.form.tax_rate_hint')"
                    >
                        <DInput
                            id="tax_rate"
                            v-model="item.tax_rate"
                            add-input-classes="required"
                            maxlength="5"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('settings.tax_exemptions.form.tax_key_invoice')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.tax_key_invoice`]"
                        :hint="__('settings.tax_exemptions.form.tax_key_invoice_hint')"
                    >
                        <DInput
                            id="tax_key_invoice"
                            v-model="item.tax_key_invoice"
                            add-label-classes="required"
                            min="0"
                            max="2147483647"
                        />
                    </DFormRow>
                    <DFormRow
                        v-if="isFinanceSystemNetsuite === false"
                        :label="__('settings.tax_exemptions.form.revenue_account_invoice')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.revenue_account_invoice`]"
                        :hint="__('settings.tax_exemptions.form.revenue_account_invoice_hint')"
                    >
                        <DInput
                            id="revenue_account_invoice"
                            v-model="item.revenue_account_invoice"
                            add-label-classes="required"
                            min="0"
                            max="2147483647"
                        />
                    </DFormRow>
                    <DFormRow
                        class="mb-4"
                        :label="__('settings.tax_exemptions.form.account_debt_loss')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.account_debt_loss`]"
                        :hint="__('settings.tax_exemptions.form.account_debt_loss_hint')"
                    >
                        <DInput
                            id="account_debt_loss"
                            v-model="item.account_debt_loss"
                            add-label-classes="required"
                            min="0"
                            max="10000000"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('settings.tax_exemptions.form.allow_credit_note')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.allow_credit_note`]"
                        :hint="__('settings.tax_exemptions.form.allow_credit_note_hint')"
                    >
                        <DFormCheck
                            id="allow_credit_note"
                            v-model="item.allow_credit_note"
                            inline
                        />
                    </DFormRow>
                    <div
                        v-if="item.allow_credit_note"
                        class="mb-4"
                    >
                        <DFormRow
                            :label="__('settings.tax_exemptions.form.tax_key_credit_note')"
                            :is-required="true"
                            :error="errorsData[`tax_exemption_rates.${item.index}.tax_key_credit_note`]"
                            :hint="__('settings.tax_exemptions.form.tax_key_credit_note_hint')"
                        >
                            <DInput
                                id="tax_key_credit_note"
                                v-model="item.tax_key_credit_note"
                                min="0"
                                max="2147483647"
                            />
                        </DFormRow>
                        <DFormRow
                            v-if="isFinanceSystemNetsuite === false"
                            :label="__('settings.tax_exemptions.form.expense_account_credit_note')"
                            :is-required="true"
                            :error="errorsData[`tax_exemption_rates.${item.index}.expense_account_credit_note`]"
                            :hint="__('settings.tax_exemptions.form.expense_account_credit_note_hint')"
                        >
                            <DInput
                                id="expense_account_credit_note"
                                v-model="item.expense_account_credit_note"
                                min="0"
                                max="2147483647"
                            />
                        </DFormRow>
                    </div>
                    <DFormRow
                        :label="__('settings.tax_exemptions.form.intercompany')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.intercompany`]"
                        :hint="__('settings.tax_exemptions.form.intercompany_hint')"
                    >
                        <DFormCheck
                            id="intercompany"
                            v-model="item.intercompany"
                            inline
                            @change="intercompanyClosed"
                        />
                    </DFormRow>
                    <DFormRow
                        v-if="item.intercompany === true && isFinanceSystemNetsuite === false"
                        :label="__('settings.tax_exemptions.form.intercompany_cost_center')"
                        :is-required="true"
                        :error="errorsData[`tax_exemption_rates.${item.index}.intercompany_cost_center`]"
                        :hint="__('settings.tax_exemptions.form.intercompany_cost_center_hint')"
                    >
                        <DInput
                            id="intercompany_cost_center"
                            v-model="item.intercompany_cost_center"
                            min="0"
                            max="2147483647"
                        />
                    </DFormRow>
                </template>
            </DynamicTable>

            <DFormRow
                :label="__('settings.tax_exemptions.form.effectivity')"
                :is-required="true"
                :hint="__('settings.tax_exemptions.form.effectivity_hint')"
            >
                <DSelect
                    id="effectivity"
                    v-model="form.effectivity"
                    :options="effectiveness"
                    add-input-classes="bg-light"
                />
                <DAlert
                    v-if="errors && errors.effectivity"
                    color="danger"
                    class="mt-2"
                >
                    {{
                        errors.effectivity
                    }}
                </DAlert>
            </DFormRow>

            <DFormRow
                :label="__('settings.tax_exemptions.form.new_customer_check')"
                :hint="__('settings.tax_exemptions.form.new_customer_check_hint')"
            >
                <DButton
                    v-for="type in searchTermTypes"
                    :key="type.id"
                    class="px-2 me-1 button-options text-center"
                    :color="form.search_term_type_id === type.id ? 'primary' : ''"
                    :label="__('settings.tax_exemptions.search_term_types.' + type.name)"
                    @click="changeSearchTermType(type)"
                />
            </DFormRow>
            <DFormRow
                v-if="searchTermType !== 'country' && form.search_term_type_id !== null"
                :label="__('settings.tax_exemptions.form.search_filter')"
                :is-required="true"
                :hint="__('settings.tax_exemptions.form.search_filter_hint')"
            >
                <DInput
                    id="search_term"
                    v-model="form.search_term"
                    min="0"
                    max="2147483647"
                />
                <DAlert
                    v-if="errors && errors.search_term"
                    color="danger"
                    class="mt-2"
                >
                    {{
                        errors.search_term
                    }}
                </DAlert>
            </DFormRow>
            <DFormRow
                v-if="searchTermType === 'country' && form.search_term_type_id !== null"
                :label="__('settings.tax_exemptions.form.country')"
                :is-required="true"
                :hint="__('settings.tax_exemptions.form.country_hint')"
            >
                <DSelect
                    id="search_term"
                    v-model="form.search_term"
                    :options="countries"
                />
                <DAlert
                    v-if="errors && errors.search_term"
                    color="danger"
                    class="mt-2"
                >
                    {{
                        errors.search_term
                    }}
                </DAlert>
            </DFormRow>
            <DFormControls
                :cancel-route="$route('settings.tax-exemption.index')"
                :dirty="form.isDirty"
                @submit="submitForm"
            />
        </DForm>
    </DCardBody>
</template>

<script>

import TranslationLink from '../TranslationLink.vue'
import DynamicTable from '~@/Components/Common/DynamicComponents/DynamicTable.vue'

export default {
    name: 'TaxExemptionForm',
    components: { DynamicTable, TranslationLink },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        toEdit: {
            type: Object,
            default: () => { return {} }
        },
        countries: {
            type: Array,
            default: () => { return [] }
        },
        defaultTenantLanguage: {
            type: Object,
            default: () => { return {} }
        },
        tenantLanguages: {
            type: Array,
            default: () => { return [] }
        },
        effectiveness: {
            type: Array,
            default: () => { return [] }
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: () => { return false }
        }
    },
    emits: ['TaxExemptionFormSubmit'],
    data () {
        return {
            form: this.$inertia.form(
                {
                    id: this.toEdit.id,
                    name: this.toEdit.name,
                    invoice_text: this.toEdit.invoice_text,
                    search_term_type_id: this.toEdit.search_term_type_id ?? null,
                    search_term: this.toEdit.search_term ?? null,
                    effectivity: this.toEdit?.effectivity ?? null,
                    language: this.defaultTenantLanguage.value,
                    tax_exemption_rates: []
                }
            ),
            searchTermType: '',
            searchTermTypes: [],
            transformers: {
                valid_from: (item) => this.setDateFormat(item, 'DD.MM.YYYY'),
                valid_until: (item) => this.setDateFormat(item, 'DD.MM.YYYY'),
                tax_rate: (item) => item ? this.taxRateFormat(item) : '',
                no_tax_on_invoice: (item) => item ? this.__('settings.tax_exemptions.index.table.values.tax_on_invoice.yes') : this.__('settings.tax_exemptions.index.table.values.tax_on_invoice.no'),
                allow_credit_note: (item) => item ? this.__('settings.tax_exemptions.index.table.values.tax_on_invoice.yes') : this.__('settings.tax_exemptions.index.table.values.tax_on_invoice.no'),
                intercompany: (item) => item ? this.__('settings.tax_exemptions.index.table.values.tax_on_invoice.yes') : this.__('settings.tax_exemptions.index.table.values.tax_on_invoice.no')
            },
            errorsData: this.errors
        }
    },
    computed: {
        getFields () {
            if (this.isFinanceSystemNetsuite === true) {
                return [
                    { key: 'valid_from', label: this.__('settings.tax_exemptions.form.valid_from') },
                    { key: 'valid_until', label: this.__('settings.tax_exemptions.form.valid_until') },
                    { key: 'no_tax_on_invoice', label: this.__('settings.tax_exemptions.form.no_tax_on_invoice') },
                    { key: 'tax_rate', label: this.__('settings.tax_exemptions.form.tax_rate') },
                    { key: 'tax_key_invoice', label: this.__('settings.tax_exemptions.form.tax_key_invoice') },
                    { key: 'account_debt_loss', label: this.__('settings.tax_exemptions.form.account_debt_loss') },
                    { key: 'allow_credit_note', label: this.__('settings.tax_exemptions.form.allow_credit_note') },
                    { key: 'tax_key_credit_note', label: this.__('settings.tax_exemptions.form.tax_key_credit_note') },
                    { key: 'intercompany', label: this.__('settings.tax_exemptions.form.intercompany') },
                    { key: 'actions', label: '' }
                ]
            }

            return [
                { key: 'valid_from', label: this.__('settings.tax_exemptions.form.valid_from') },
                { key: 'valid_until', label: this.__('settings.tax_exemptions.form.valid_until') },
                { key: 'no_tax_on_invoice', label: this.__('settings.tax_exemptions.form.no_tax_on_invoice') },
                { key: 'tax_rate', label: this.__('settings.tax_exemptions.form.tax_rate') },
                { key: 'tax_key_invoice', label: this.__('settings.tax_exemptions.form.tax_key_invoice') },
                { key: 'revenue_account_invoice', label: this.__('settings.tax_exemptions.form.revenue_account_invoice') },
                { key: 'account_debt_loss', label: this.__('settings.tax_exemptions.form.account_debt_loss') },
                { key: 'allow_credit_note', label: this.__('settings.tax_exemptions.form.allow_credit_note') },
                { key: 'tax_key_credit_note', label: this.__('settings.tax_exemptions.form.tax_key_credit_note') },
                { key: 'expense_account_credit_note', label: this.__('settings.tax_exemptions.form.expense_account_credit_note') },
                { key: 'intercompany', label: this.__('settings.tax_exemptions.form.intercompany') },
                { key: 'intercompany_cost_center', label: this.__('settings.tax_exemptions.form.intercompany_cost_center') },
                { key: 'actions', label: '' }
            ]
        }
    },
    watch: {
        errors (newErrors) {
            this.errorsData = newErrors
        }
    },
    created () {
        this.setExemptionRates(this.toEdit?.tax_exemption_rates ?? [])
    },
    mounted () {
        this.getSearchTermTypes()
    },
    methods: {
        submitForm () {
            this.$emit('TaxExemptionFormSubmit', this.form)
        },
        intercompanyClosed () {
            if (this.form.intercompany === false) {
                this.form.intercompany_cost_center = null
            }
        },
        getSearchTermTypes () {
            return window.axios.get(this.$route('settings.tax-exemption.search-term-types')).then(
                response => {
                    response.data.forEach((row) => {
                        this.searchTermTypes.push({ id: row.id, name: row.name })
                        if (this.form.search_term_type_id === row.id) {
                            this.searchTermType = row.name
                        }
                    })
                })
        },
        changeSearchTermType (item) {
            if (this.form.search_term_type_id === item.id) {
                this.form.search_term_type_id = null
                this.searchTermType = ''
                this.form.search_term = null
            } else {
                this.form.search_term_type_id = item.id
                this.searchTermType = item.name
                if (this.searchTermType !== 'country') {
                    this.form.search_term = null
                }
            }
        },
        getCountries () {
            return window.axios.get(this.$route('settings.tax-exemption.search-term-types')).then(
                response => {
                    response.data.forEach((row) => {
                        this.searchTermTypes.push({ id: row.id, name: row.name })
                    })
                })
        },
        setExemptionRates (data) {
            this.errorsData = {}
            // Sort by start date
            data = data.sort(function (a, b) {
                // Turn your strings into dates, and then subtract them
                // to get a value that is either negative, positive, or zero.
                return new Date(b.valid_from) - new Date(a.valid_from)
            })

            const rates = []

            let indexTrack = -1

            data.forEach((rate, index) => {
                const startDateOfNextRecord = data[index - 1]?.valid_from ?? null
                const date = startDateOfNextRecord ? new Date(startDateOfNextRecord) : null

                rates.push({
                    index: ++indexTrack,
                    id: rate.id,
                    valid_from: rate.valid_from,
                    valid_until: date !== null ? new Date(date.setDate(date.getDate() - 1)).toISOString() : null,
                    tax_key_invoice: rate.tax_key_invoice,
                    revenue_account_invoice: rate.revenue_account_invoice,
                    no_tax_on_invoice: rate.no_tax_on_invoice === undefined ? false : rate.no_tax_on_invoice,
                    account_debt_loss: rate.account_debt_loss,
                    tax_key_credit_note: rate.tax_key_credit_note,
                    expense_account_credit_note: rate.expense_account_credit_note,
                    allow_credit_note: rate.allow_credit_note === undefined ? false : rate.allow_credit_note,
                    intercompany: rate.intercompany === undefined ? false : rate.intercompany,
                    intercompany_cost_center: rate.intercompany_cost_center ?? null,
                    tax_rate: rate.tax_rate ?? 0
                })
            })

            this.form.tax_exemption_rates = rates
        },
        getNewRowData () {
            return {
                index: 0,
                id: null,
                valid_from: null,
                valid_until: null,
                tax_key_invoice: null,
                revenue_account_invoice: this.isFinanceSystemNetsuite === false ? null : 0,
                no_tax_on_invoice: false,
                account_debt_loss: null,
                tax_key_credit_note: null,
                expense_account_credit_note: null,
                allow_credit_note: false,
                intercompany: false,
                intercompany_cost_center: null,
                tax_rate: null
            }
        },
        taxRateFormat (value) {
            return parseFloat(value).toFixed(2)
        }
    }
}
</script>

<style scoped>
.button-options {
    width: 8vw;
}
</style>
